/**
 * 시방 기반 카메라 배치 자동 제안 (설치모드 + L×W → H·대수·각도·화각).
 * L = pool Length (가로/장변, m)
 * W = pool Width (세로/폭, m)
 * H = install Height (수면↔렌즈) — 면적·모드 표에서 자동
 *
 * 근거: docs/수영장_AI카메라_설치시방서_면적별높이각도_20260929_145613.md
 */
import { layoutDefaultOptics, DEFAULT_FOCAL_LENGTH_MM } from './cameraOptics';
import {
  DEFAULT_WALL_POOL_EDGE_OFFSET_M,
  DEFAULT_WALL_POOL_EDGE_OFFSETS_M,
  type WallPoolEdgeOffsetsM,
  uniformWallPoolEdgeOffsets,
} from './cameraGridPlacement';

export type SpecMountMode = 'ceiling' | 'wall';
/** 고정 초점 렌즈 vs 가변(광학 줌) */
export type SpecLensMode = 'fixed' | 'variable';

export type CameraSpecProposal = {
  lengthM: number;
  widthM: number;
  /** 자동 산정(또는 오버라이드) 설치 높이 */
  heightM: number;
  /** true면 시방 표 대표값으로 산정 */
  heightAuto: boolean;
  areaM2: number;
  mountMode: SpecMountMode;
  lensMode: SpecLensMode;
  cameraCount: number;
  ceilingRows: number;
  ceilingCols: number;
  wallRows: number;
  wallCols: number;
  wallLeftCount: number;
  wallRightCount: number;
  ceilingTiltDeg: number;
  wallTiltDeg: number;
  focalLengthMm: number;
  fovHDeg: number;
  fovVDeg: number;
  resolutionW: number;
  resolutionH: number;
  /** @deprecated wallPoolEdgeOffsetsM 권장 */
  wallPoolEdgeOffsetM: number;
  wallPoolEdgeOffsetsM: WallPoolEdgeOffsetsM;
  wallMountOffsetM: number;
  rationale: string[];
  areaBandLabel: string;
};

const FOCAL_MM = DEFAULT_FOCAL_LENGTH_MM; // 2.8
const EDGE_M = DEFAULT_WALL_POOL_EDGE_OFFSET_M;
const WALL_MOUNT_M = 0.2;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** 면적 밴드 — 천장/벽 대표 높이·대수 (시방서 §2.2 / §3.2) */
function areaBand(area: number): {
  label: string;
  ceilingN: [number, number];
  wallN: [number, number];
  ceilingH: number;
  wallH: number;
  ceilingTilt: number;
  wallTiltHint: number;
} {
  if (area < 100) {
    return {
      label: 'A < 100 m²',
      ceilingN: [2, 3],
      wallN: [3, 4],
      ceilingH: 4.0,
      wallH: 3.0,
      ceilingTilt: 90,
      wallTiltHint: 24,
    };
  }
  if (area < 200) {
    return {
      label: '100 ≤ A < 200',
      ceilingN: [3, 4],
      wallN: [4, 6],
      ceilingH: 4.2,
      wallH: 3.1,
      ceilingTilt: 90,
      wallTiltHint: 22,
    };
  }
  if (area < 350) {
    return {
      label: '200 ≤ A < 350 (25m급)',
      ceilingN: [4, 6],
      wallN: [6, 8],
      ceilingH: 4.5,
      wallH: 3.2,
      ceilingTilt: 90,
      wallTiltHint: 20,
    };
  }
  if (area < 550) {
    return {
      label: '350 ≤ A < 550',
      ceilingN: [6, 8],
      wallN: [8, 10],
      ceilingH: 4.8,
      wallH: 3.4,
      ceilingTilt: 90,
      wallTiltHint: 20,
    };
  }
  if (area < 900) {
    return {
      label: '550 ≤ A < 900',
      ceilingN: [8, 12],
      wallN: [10, 14],
      ceilingH: 5.2,
      wallH: 3.6,
      ceilingTilt: 85,
      wallTiltHint: 20,
    };
  }
  return {
    label: 'A ≥ 900',
    ceilingN: [12, 16],
    wallN: [14, 18],
    ceilingH: 5.5,
    wallH: 3.8,
    ceilingTilt: 80,
    wallTiltHint: 20,
  };
}

function ceilingGrid(n: number, lengthM: number, widthM: number): { rows: number; cols: number } {
  if (n <= 0) return { rows: 0, cols: 0 };
  if (n === 1) return { rows: 1, cols: 1 };
  const aspect = lengthM / Math.max(widthM, 0.1);
  let cols = Math.max(1, Math.round(Math.sqrt(n * aspect)));
  let rows = Math.max(1, Math.ceil(n / cols));
  while (rows * cols < n) cols += 1;
  while (rows * cols > n && rows * cols - n >= Math.min(rows, cols)) {
    if (cols >= rows && cols > 1) cols -= 1;
    else if (rows > 1) rows -= 1;
    else break;
    if (rows * cols < n) {
      cols = Math.ceil(n / rows);
      break;
    }
  }
  return { rows: clamp(rows, 1, 12), cols: clamp(cols, 1, 12) };
}

function wallGrid(n: number): {
  wallRows: number;
  wallCols: number;
  wallLeftCount: number;
  wallRightCount: number;
} {
  if (n <= 0) {
    return { wallRows: 0, wallCols: 0, wallLeftCount: 0, wallRightCount: 0 };
  }
  if (n === 1) {
    return { wallRows: 1, wallCols: 1, wallLeftCount: 0, wallRightCount: 0 };
  }
  if (n === 2) {
    return { wallRows: 2, wallCols: 1, wallLeftCount: 0, wallRightCount: 0 };
  }
  if (n === 3) {
    return { wallRows: 2, wallCols: 1, wallLeftCount: 1, wallRightCount: 0 };
  }
  if (n === 4) {
    return { wallRows: 2, wallCols: 2, wallLeftCount: 0, wallRightCount: 0 };
  }
  if (n === 5) {
    return { wallRows: 2, wallCols: 2, wallLeftCount: 1, wallRightCount: 0 };
  }
  // n≥6: 장변 위·아래를 우선 (위3/아래3 = 2×3). 남는 대수는 좌·우 단변.
  // 예: 6→위3/아래3, 7→+좌1, 8→+좌1/우1, 9→위4/아래4, 10→위4/아래4+좌우…
  const wallRows = 2;
  let wallCols = 3;
  let left = 0;
  let right = 0;
  let rem = n - wallRows * wallCols; // 6대 기준 잔여
  if (rem < 0) {
    // n=6 미만은 위에서 처리. 방어적으로 열 축소
    wallCols = Math.max(1, Math.floor(n / 2));
    rem = n - wallRows * wallCols;
  }
  while (rem >= 2 && wallCols < 12) {
    // 잔여 2대 이상이면 장변 열을 늘리는 편이 구석에 유리한 경우도 있음 →
    // 우선 단변에 배치하고, 단변이 찬 뒤 열 증가
    break;
  }
  if (rem >= 1) {
    left = 1;
    rem -= 1;
  }
  if (rem >= 1) {
    right = 1;
    rem -= 1;
  }
  // 여전히 남으면 장변 열 증가 (위4/아래4 …)
  while (rem > 0 && wallCols < 12) {
    wallCols += 1;
    rem -= 2;
    if (rem < 0) {
      // 홀수 1대 남음 → 좌 보강
      if (left < 1) left = 1;
      else if (right < 1) right = 1;
      rem = 0;
    }
  }
  // rem이 -1인 경우(열만 증가) 대수 맞춤은 호출측에서 rows*cols+left+right로 확정
  let total = wallRows * wallCols + left + right;
  while (total > n && wallCols > 1) {
    wallCols -= 1;
    total = wallRows * wallCols + left + right;
  }
  while (total > n && right > 0) {
    right -= 1;
    total = wallRows * wallCols + left + right;
  }
  while (total > n && left > 0) {
    left -= 1;
    total = wallRows * wallCols + left + right;
  }
  return {
    wallRows,
    wallCols: clamp(wallCols, 1, 12),
    wallLeftCount: left,
    wallRightCount: right,
  };
}

/** 벽 하향각: 축을 수면 0.6W 지점으로 (근거문서 §4.3) */
export function wallTiltFromHeightAndWidth(heightM: number, widthM: number): number {
  const dEdge = EDGE_M - WALL_MOUNT_M; // ≈0.8
  const dAim = dEdge + 0.6 * Math.max(widthM, 1);
  const rad = Math.atan(heightM / dAim);
  const deg = (rad * 180) / Math.PI;
  return Math.round(clamp(deg, 18, 28) * 10) / 10;
}

/** 면적·모드 → 시방 대표 설치 높이 */
export function recommendInstallHeightM(
  lengthM: number,
  widthM: number,
  mountMode: SpecMountMode,
): number {
  const L = clamp(Number(lengthM) || 25, 3, 120);
  const W = clamp(Number(widthM) || 12, 2, 80);
  const band = areaBand(L * W);
  return mountMode === 'wall' ? band.wallH : band.ceilingH;
}

export function proposeCameraSpec(input: {
  lengthM: number;
  widthM: number;
  mountMode: SpecMountMode;
  /** 생략 시 시방 표 대표 높이 자동 */
  heightM?: number | null;
  /** 기본 fixed — 수영장 AI 납품 다수 */
  lensMode?: SpecLensMode;
  /** 고정/가변 공통 기준 초점. 고정이면 이 값 잠금. 기본 2.8 */
  focalLengthMm?: number | null;
}): CameraSpecProposal {
  const lengthM = clamp(Number(input.lengthM) || 25, 3, 120);
  const widthM = clamp(Number(input.widthM) || 12, 2, 80);
  const mountMode = input.mountMode === 'wall' ? 'wall' : 'ceiling';
  const lensMode: SpecLensMode = input.lensMode === 'variable' ? 'variable' : 'fixed';
  const focalLengthMm = clamp(
    Number(input.focalLengthMm) > 0 ? Number(input.focalLengthMm) : FOCAL_MM,
    1.8,
    16,
  );
  const areaM2 = lengthM * widthM;
  const band = areaBand(areaM2);

  const autoH = mountMode === 'wall' ? band.wallH : band.ceilingH;
  const rawOverride = input.heightM;
  const hasOverride =
    rawOverride != null && Number.isFinite(Number(rawOverride)) && Number(rawOverride) > 0;
  const heightAuto = !hasOverride;
  const heightM = clamp(hasOverride ? Number(rawOverride) : autoH, 2.0, 12);

  const [n0, n1] = mountMode === 'ceiling' ? band.ceilingN : band.wallN;
  let cameraCount = Math.round((n0 + n1) / 2);
  // 벽면: 25m급(6~8대)은 위3/아래3(6대)을 기본으로 — 상2/하2/좌1/우1보다 구석에 유리
  if (mountMode === 'wall' && n0 >= 6 && n1 <= 8) {
    cameraCount = 6;
  } else if (mountMode === 'wall' && n0 >= 8 && cameraCount >= 7 && cameraCount <= 9) {
    // 그 이상 밴드도 장변 3열(6) + 단변 보강을 선호
    cameraCount = Math.min(n1, Math.max(6, cameraCount));
  }
  // 수동 높이가 대표에서 크게 벗어나면 대수 보정
  if (!heightAuto) {
    const delta = heightM - autoH;
    if (mountMode === 'ceiling') {
      if (delta > 0.4) cameraCount += 1;
      if (delta > 0.9) cameraCount += 1;
      if (delta < -0.4) cameraCount = Math.max(n0, cameraCount - 1);
    } else {
      if (delta > 0.3) cameraCount += 1;
      if (delta < -0.3) cameraCount = Math.max(n0, cameraCount - 1);
    }
  }
  cameraCount = clamp(cameraCount, n0, Math.max(n1, cameraCount));

  const optics = layoutDefaultOptics(focalLengthMm, 1920, 1080);
  const rationale: string[] = [];
  rationale.push(
    `입력: 모드=${mountMode === 'ceiling' ? '천장' : '벽면'}, L=${lengthM} m, W=${widthM} m → A=${areaM2.toFixed(0)} m² (${band.label}).`,
  );
  rationale.push(
    heightAuto
      ? `설치 높이 H=${heightM} m (시방 ${band.label} ${mountMode === 'ceiling' ? '천장' : '벽면'} 대표값 자동).`
      : `설치 높이 H=${heightM} m (수동 지정, 표 대표 ${autoH} m).`,
  );
  rationale.push(
    lensMode === 'fixed'
      ? `렌즈 고정 ${focalLengthMm} mm (H/V 잠금) → HFOV≈${optics.fov_h_deg}°, VFOV≈${optics.fov_v_deg}° @1080p. 커버는 위치·팬·틸트로 맞춤.`
      : `렌즈 가변(줌) 기준 ${focalLengthMm} mm → 자동 배치 시 커버에 맞춰 초점·H/V 조정 가능. 시작 HFOV≈${optics.fov_h_deg}°, VFOV≈${optics.fov_v_deg}°.`,
  );

  let ceilingRows = 0;
  let ceilingCols = 0;
  let wallRows = 0;
  let wallCols = 0;
  let wallLeftCount = 0;
  let wallRightCount = 0;
  const ceilingTiltDeg = band.ceilingTilt;
  const geomWallTilt = wallTiltFromHeightAndWidth(heightM, widthM);
  // 기하 tilt와 표 힌트의 중간을 반올림해 18~28° 클램프
  let wallTiltDeg =
    Math.round(((geomWallTilt + band.wallTiltHint) / 2) * 10) / 10;
  wallTiltDeg = clamp(wallTiltDeg, 18, 28);

  if (mountMode === 'ceiling') {
    const g = ceilingGrid(cameraCount, lengthM, widthM);
    ceilingRows = g.rows;
    ceilingCols = g.cols;
    cameraCount = ceilingRows * ceilingCols;
    rationale.push(
      `천장: 하향 ${ceilingTiltDeg}°, 격자 ${ceilingRows}×${ceilingCols}=${cameraCount}대. 방위는 각 칸 직하(시뮬 자동).`,
    );
    if (heightM > 5.2) {
      rationale.push('주의: H>5.2 m @1080p는 머리 px가 빠듯 가능. 대수↑ 또는 1440p 검토.');
    }
  } else {
    const g = wallGrid(cameraCount);
    wallRows = g.wallRows;
    wallCols = g.wallCols;
    wallLeftCount = g.wallLeftCount;
    wallRightCount = g.wallRightCount;
    cameraCount = wallRows * wallCols + wallLeftCount + wallRightCount;
    rationale.push(
      `벽면: 하향 ${wallTiltDeg}° — 장변 위 ${wallCols}/아래 ${wallCols}` +
        (wallLeftCount || wallRightCount
          ? ` + 좌 ${wallLeftCount}/우 ${wallRightCount}`
          : '') +
        ` = ${cameraCount}대 (위·아래 장변 밀도 우선).`,
    );
    rationale.push('방위(heading): 각 카메라가 수면 중심을 향하도록 시뮬레이터 자동 산출.');
  }

  // 장변만 쓸 때 좌·우 데크 1m 기본값을 넣지 않음(도면 좌우 공백 과다 방지)
  const wallPoolEdgeOffsetsM =
    mountMode === 'wall'
      ? {
          top: EDGE_M,
          bottom: EDGE_M,
          left: wallLeftCount > 0 ? EDGE_M : 0,
          right: wallRightCount > 0 ? EDGE_M : 0,
        }
      : { ...DEFAULT_WALL_POOL_EDGE_OFFSETS_M };

  return {
    lengthM,
    widthM,
    heightM,
    heightAuto,
    areaM2,
    mountMode,
    lensMode,
    cameraCount,
    ceilingRows,
    ceilingCols,
    wallRows,
    wallCols,
    wallLeftCount,
    wallRightCount,
    ceilingTiltDeg,
    wallTiltDeg,
    focalLengthMm,
    fovHDeg: optics.fov_h_deg,
    fovVDeg: optics.fov_v_deg,
    resolutionW: 1920,
    resolutionH: 1080,
    wallPoolEdgeOffsetM: wallPoolEdgeOffsetsM.top,
    wallPoolEdgeOffsetsM,
    wallMountOffsetM: WALL_MOUNT_M,
    rationale,
    areaBandLabel: band.label,
  };
}

export type CameraSpecSeed = {
  poolLengthM: number;
  poolWidthM: number;
  installHeightM: number;
  mountMode: SpecMountMode;
  lensMode: SpecLensMode;
  ceilingRows: number;
  ceilingCols: number;
  wallRows: number;
  wallCols: number;
  wallLeftCount: number;
  wallRightCount: number;
  focalLengthMm: number;
  ceilingTiltDeg: number;
  wallTiltDeg: number;
  /** 구버전 시드 호환 — 없으면 wallPoolEdgeOffsetsM 또는 1.0 */
  wallPoolEdgeOffsetM?: number;
  wallPoolEdgeOffsetsM?: WallPoolEdgeOffsetsM;
  wallMountOffsetM: number;
  resolutionW: number;
  resolutionH: number;
};

export function seedWallPoolEdgeOffsets(seed: CameraSpecSeed): WallPoolEdgeOffsetsM {
  if (seed.wallPoolEdgeOffsetsM) {
    return { ...DEFAULT_WALL_POOL_EDGE_OFFSETS_M, ...seed.wallPoolEdgeOffsetsM };
  }
  if (seed.wallPoolEdgeOffsetM != null) {
    return uniformWallPoolEdgeOffsets(seed.wallPoolEdgeOffsetM);
  }
  return { ...DEFAULT_WALL_POOL_EDGE_OFFSETS_M };
}

export function proposalToSeed(p: CameraSpecProposal): CameraSpecSeed {
  const edges = p.wallPoolEdgeOffsetsM || uniformWallPoolEdgeOffsets(p.wallPoolEdgeOffsetM);
  return {
    poolLengthM: p.lengthM,
    poolWidthM: p.widthM,
    installHeightM: p.heightM,
    mountMode: p.mountMode,
    lensMode: p.lensMode,
    ceilingRows: p.ceilingRows,
    ceilingCols: p.ceilingCols,
    wallRows: p.wallRows,
    wallCols: p.wallCols,
    wallLeftCount: p.wallLeftCount,
    wallRightCount: p.wallRightCount,
    focalLengthMm: p.focalLengthMm,
    ceilingTiltDeg: p.ceilingTiltDeg,
    wallTiltDeg: p.wallTiltDeg,
    wallPoolEdgeOffsetM: edges.top,
    wallPoolEdgeOffsetsM: edges,
    wallMountOffsetM: p.wallMountOffsetM,
    resolutionW: p.resolutionW,
    resolutionH: p.resolutionH,
  };
}
